import { useEffect, useState } from 'preact/hooks';
import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import { Knot } from '../components/Knot';
import {
  getAuthMethods,
  passwordLogin,
  passwordRegister,
  passwordResetRequest,
  passkeyLoginOptions,
  passkeyLoginVerify,
  ApiError,
  type ApiUser,
  type AuthMethods,
} from '../lib/api';
import './Login.css';

export interface LoginProps {
  /** Set when App.tsx's magic-link-landing handling itself failed (an
   *  expired/already-used token) — shown once, above the form. */
  initialError?: string;
  /** Handed the freshly signed-in user (password/register/passkey). App then
   *  runs tenant resolution (one -> Today, none -> onboarding, many -> picker).
   *  `justRegistered` carries the calm "we emailed you a link to confirm your
   *  address" notice forward into onboarding. */
  onAuthenticated: (user: ApiUser, opts?: { justRegistered?: boolean }) => void;
}

type Mode = 'signin' | 'register';
// Which action is currently in flight (so only that button shows "…", and the
// whole form disables while any one runs).
type Busy = null | 'password' | 'passkey' | 'reset';

// Auth-2: the everyday sign-in gate. NEVER bounces a returning user to email
// (Huan 2026-07-13): four methods, one-tap first (Continue with Google / Apple
// — only where /auth/methods says configured — and Passkey / Face·Touch ID),
// then email + password below a subtle divider. Magic link is demoted to
// email-verification + new-device recovery and no longer lives here. Matches
// the blessed onboarding_flow.html welcome: the breathing Knot, the one-line
// pitch, the night palette, the calm voice.
export function Login({ initialError, onAuthenticated }: LoginProps) {
  const [methods, setMethods] = useState<AuthMethods | null>(null);
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(initialError ?? null);
  // The "forgot password? -> check your email" sub-state. Once sent, we show
  // the calm confirmation instead of the form (anti-enumeration: same state
  // whether or not the email exists).
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAuthMethods()
      .then((m) => {
        if (!cancelled) setMethods(m);
      })
      .catch(() => {
        // A failed capabilities probe must never lock the user out — fall
        // back to the always-available methods (password + passkey).
        if (!cancelled) setMethods({ password: true, passkey: true, google: false, apple: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function toggleMode() {
    setMode((m) => (m === 'signin' ? 'register' : 'signin'));
    setError(null);
  }

  async function submitPassword() {
    const e = email.trim();
    if (!e || !password) return;
    setBusy('password');
    setError(null);
    try {
      if (mode === 'signin') {
        const user = await passwordLogin(e, password);
        onAuthenticated(user);
      } else {
        const { user } = await passwordRegister(e, password);
        onAuthenticated(user, { justRegistered: true });
      }
    } catch (err) {
      setBusy(null);
      if (mode === 'register' && err instanceof ApiError && err.status === 409) {
        setError('An account with this email already exists. Try signing in instead.');
      } else if (mode === 'register' && err instanceof ApiError && err.code === 'weak_password') {
        setError(err.message);
      } else if (mode === 'signin') {
        // The server's generic, non-enumerating message — surfaced calmly.
        setError('Email or password is incorrect.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  }

  async function doPasskey() {
    const e = email.trim();
    if (!browserSupportsWebAuthn()) {
      setError('Passkeys aren’t available on this device yet. Use another method.');
      return;
    }
    if (!e) {
      setError('Enter your email first, then use Face / Touch ID.');
      return;
    }
    setBusy('passkey');
    setError(null);
    try {
      const { ceremonyId, options } = await passkeyLoginOptions(e);
      const response = await startAuthentication({
        optionsJSON: options as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      const user = await passkeyLoginVerify(ceremonyId, response);
      onAuthenticated(user);
    } catch {
      setBusy(null);
      setError('We couldn’t sign you in with a passkey. Try another method.');
    }
  }

  async function submitReset() {
    const e = email.trim();
    if (!e) {
      setError('Enter your email, then we’ll send a reset link.');
      return;
    }
    setBusy('reset');
    setError(null);
    try {
      await passwordResetRequest(e);
      setResetSent(true);
    } catch {
      // reset-request is always 202 server-side; only a network failure lands
      // here — still show the calm sent state rather than an alarm.
      setResetSent(true);
    } finally {
      setBusy(null);
    }
  }

  function oauthRedirect(provider: 'google' | 'apple') {
    window.location.href = `/auth/oauth/${provider}/start`;
  }

  const disabled = busy !== null;

  if (resetSent) {
    return (
      <div class="login">
        <div class="login__welcome">
          <Knot size={46} />
          <div class="login__eyebrow">Check your inbox</div>
          <div class="login__say">
            If an account exists for
            <br />
            <b class="login__strong">{email.trim()}</b>,<br />
            <span class="login__say-soft">a reset link is on its way.</span>
          </div>
        </div>
        <button
          type="button"
          class="login__textlink"
          onClick={() => {
            setResetSent(false);
            setError(null);
          }}
        >
          &lsaquo; Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div class="login">
      <div class="login__welcome">
        <Knot size={46} />
        <div class="login__eyebrow">Vowric</div>
        <div class="login__say">
          Your career,
          <br />
          held together.
          <br />
          <span class="login__say-soft">
            Auditions, tapes, bookings and money — in one calm place.
          </span>
        </div>
      </div>

      {error ? (
        <div class="login__error" role="alert">
          {error}
        </div>
      ) : null}

      {/* One-tap methods first. */}
      {methods?.google ? (
        <button
          type="button"
          class="login__btn login__btn--method"
          disabled={disabled}
          onClick={() => oauthRedirect('google')}
        >
          Continue with Google
        </button>
      ) : null}
      {methods?.apple ? (
        <button
          type="button"
          class="login__btn login__btn--method"
          disabled={disabled}
          onClick={() => oauthRedirect('apple')}
        >
          Continue with Apple
        </button>
      ) : null}
      {methods?.passkey ? (
        <button
          type="button"
          class="login__btn login__btn--method"
          disabled={disabled}
          onClick={() => void doPasskey()}
        >
          {busy === 'passkey' ? 'Waiting for Face / Touch ID…' : 'Passkey — Face / Touch ID'}
        </button>
      ) : null}

      <div class="login__divider">
        <span>or {mode === 'signin' ? 'sign in with email' : 'create an account'}</span>
      </div>

      <label class="login__label" for="login-email">
        Email
      </label>
      <input
        id="login-email"
        class="login__input"
        type="email"
        inputMode="email"
        autocomplete="email"
        placeholder="you@example.com"
        value={email}
        disabled={disabled}
        onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
      />

      <label class="login__label" for="login-password">
        Password
      </label>
      <input
        id="login-password"
        class="login__input"
        type="password"
        autocomplete={mode === 'signin' ? 'current-password' : 'new-password'}
        placeholder={mode === 'signin' ? 'Your password' : 'Choose a password'}
        value={password}
        disabled={disabled}
        onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submitPassword();
        }}
      />

      <button
        type="button"
        class="login__btn"
        disabled={disabled || !email.trim() || !password}
        onClick={() => void submitPassword()}
      >
        {busy === 'password'
          ? mode === 'signin'
            ? 'Signing in…'
            : 'Creating account…'
          : mode === 'signin'
            ? 'Sign in'
            : 'Create account'}
      </button>

      {mode === 'signin' ? (
        <button type="button" class="login__textlink" disabled={disabled} onClick={() => void submitReset()}>
          Forgot password?
        </button>
      ) : null}

      <div class="login__toggle">
        {mode === 'signin' ? "New here? " : 'Already have an account? '}
        <button type="button" class="login__toggle-btn" disabled={disabled} onClick={toggleMode}>
          {mode === 'signin' ? 'Create an account' : 'Sign in'}
        </button>
      </div>
    </div>
  );
}
